
// Utility function to calculate result levels based on panel format and data
// This file handles the calculation of result levels for different panel formats

// Import score configuration files for different panel formats
import { scoreConfig_AllergyandEczema as scoreConfigQPT } from '../configs/pdfScoreConfig.js';
import { scoreConfig_AllergyandEczema_9P as scoreConfig9P } from '../configs/9P/pdfScoreConfig_9P.js';
import { scoreConfig_AllergyandEczema_allergy as scoreConfigAllergy } from '../configs/allergy/pdfScoreConfig_allergy.js';
import { scoreConfig_AllergyandEczema_allergy_kids as scoreConfigAllergyKids } from '../configs/allergy_kids/pdfScoreConfig_allergy_kids.js';
import { scoreConfig_AllergyandEczema_toxin as scoreConfigToxin } from '../configs/Toxin/pdfScoreConfig_toxin.js';
import { scoreConfig_AllergyandEczema_oms as scoreConfigOms } from '../configs/oms/pdfScoreConfig_oms.js';
import { scoreConfig_AllergyandEczema_bwcallergy as scoreConfigBwcallergy } from '../configs/bwc_allergy/pdfScoreConfig_bwcallergy.js';
import { scoreConfig_AllergyandEczema_bwccore as scoreConfigBwccore } from '../configs/bwc_core/pdfScoreConfig_bwccore.js';
import { scoreConfig_AllergyandEczema_bwcdiamond as scoreConfigBwcdiamond } from '../configs/bwc_diamond/pdfScoreConfig_bwcdiamond.js';

// Import the real bacteria scoring configuration
import { 
  bacteriaMetaConfig,
  bacteriaMetaConfig_9P,
  bacteriaMetaConfig_allergy,
  bacteriaMetaConfig_toxin,
  bacteriaMetaConfig_oms,
  bacteriaMetaConfig_bwcallergy,
  bacteriaMetaConfig_bwccore,
  bacteriaMetaConfig_bwcdiamond,
  bacteriaMetaConfig_allergy_kids
} from '../configs/calculate_ctvalue.js';

// Import the bacteria condition evaluator (same as preview)
import { evaluateBacteriaCondition } from './bacteriaEvaluator.js';

// Hot-path logging: this module runs per visible History row (and used to run on every
// re-render), so its ~40 console.log calls were a major source of UI jank. They are now
// no-ops unless you set localStorage.DEBUG_RESULT_CALC = '1' and reload.
const RESULT_CALC_DEBUG = (() => { try { return localStorage.getItem('DEBUG_RESULT_CALC') === '1'; } catch (e) { return false; } })();
const debugLog = (...args) => { if (RESULT_CALC_DEBUG) console.log(...args); };

// Function to get score configuration based on panel format
const getScoreConfig = (panelFormat) => {
  switch (panelFormat) {
    case 'QU':
    case 'QPT':
      return scoreConfigQPT;
    case 'Q9':  // Database value for 9P format
    case '9P':
      return scoreConfig9P;
    case 'QA':  // Database value for Allergy and Skin format
    case 'Allergy and Skin':
      return scoreConfigAllergy;
    case 'QI':  // Display value for Infant Allergy Biome Panel
    case 'QA(KIDs)':  // Database value for Allergy and Skin Kids format
    case 'Allergy and Skin_kids':
      return scoreConfigAllergyKids;
    case 'QG':  // Database value for Toxin format
    case 'Toxin':
      return scoreConfigToxin;
    case 'QM':  // Database value for oms format
    case 'oms':
      return scoreConfigOms;
    case 'QBWCA':  // Database value for bwcallergy format
    case 'bwcallergy':
      return scoreConfigBwcallergy;
    case 'QBWCE':  // Database value for bwccore format
    case 'bwccore':
      return scoreConfigBwccore;
    case 'QBWCU':  // Database value for bwcdiamond format
    case 'bwcdiamond':
      return scoreConfigBwcdiamond;
    default:
      return [];
  }
};

// Function to calculate static asthma score using the EXACT SAME LOGIC as PdfStaticModal
const calculateStaticAsthmaScore = (reportData, config, panelFormat) => {
  try {
    if (!config.value_use || config.value_use.length === 0) {
      console.warn('🔍 No value_use configured for:', config.titel);
      return null;
    }

    // Validate report data
    if (!reportData || typeof reportData !== 'object') {
      console.error('🔍 Invalid report data provided');
      return null;
    }

    debugLog('🔍 Calculating static asthma score for:', config.titel);
    debugLog('🔍 Panel format:', panelFormat);
    debugLog('🔍 Bacteria to use:', config.value_use);
    
    // COPY THE EXACT SAME getScore FUNCTION FROM PdfStaticModal.js
    const getScore = (label) => {
      const raw = parseFloat(reportData[label]) || 0;
      const universal = parseFloat(reportData['Universal bacteria']) || 0;
      
      // Get the correct meta config based on panel format (SAME AS PREVIEW)
      const metaSource = 
        panelFormat === 'Q9' || panelFormat === '9P'
          ? bacteriaMetaConfig_9P
          : panelFormat === 'QA' || panelFormat === 'Allergy and Skin'
          ? bacteriaMetaConfig_allergy
          : panelFormat === 'QI' || panelFormat === 'QA(KIDs)' || panelFormat === 'Allergy and Skin_kids'
          ? bacteriaMetaConfig_allergy_kids
          : panelFormat === 'QG' || panelFormat === 'Toxin'
          ? bacteriaMetaConfig_toxin
          : panelFormat === 'QM' || panelFormat === 'oms'
          ? bacteriaMetaConfig_oms
          : panelFormat === 'QBWCA' || panelFormat === 'bwcallergy'
          ? bacteriaMetaConfig_bwcallergy
          : panelFormat === 'QBWCE' || panelFormat === 'bwccore'
          ? bacteriaMetaConfig_bwccore
          : panelFormat === 'QBWCU' || panelFormat === 'bwcdiamond'
          ? bacteriaMetaConfig_bwcdiamond
          : bacteriaMetaConfig;
      
      debugLog(`🔍 [${panelFormat}] Using meta config for ${label}:`, metaSource === bacteriaMetaConfig ? 'default' : 'specific');

      const meta = metaSource[label];
      if (meta && Array.isArray(meta.rangeMapping)) {
        let matched;
        if (meta.customCondition) {
          // For custom conditions, use evaluateBacteriaCondition (SAME AS PREVIEW)
          matched = meta.rangeMapping.find(r => 
            typeof r.condition === 'function' && evaluateBacteriaCondition(r.condition, raw, reportData, label)
          );
        } else {
          // For simple range conditions
          matched = meta.rangeMapping.find(r => raw >= r.min && raw <= r.max);
        }
         
         if (matched && matched.score_cal !== undefined) {
           return matched.score_cal;
         }
       }
       return 0; // Default to 0 (same as preview)
     };

     // Get the number of bacteria being used (n)
     const n = config.value_use.length;
     
     // Calculate sum of absolute differences from score 3
     let sumDifferences = 0;
     const bacteriaScores = [];
     
     // Only show detailed logs for Asthma (same as preview)
     const isAsthma = config.match_key === 'Asthma' || 
                      (config.titel && typeof config.titel === 'string' && config.titel.includes('Asthma')) ||
                      (config.titel && Array.isArray(config.titel) && config.titel.some(t => t.includes('Asthma')));
     
     if (isAsthma) {
       debugLog('=== Asthma Score Calculation (SAME AS PREVIEW) ===');
       debugLog('Timestamp:', new Date().toISOString());
       debugLog('Universal bacteria:', reportData['Universal bacteria']);
       debugLog('Number of bacteria (n):', n);
       debugLog('Config value_use:', config.value_use);
     }
     
     config.value_use.forEach((bacteria, index) => {
       const score_cal = getScore(bacteria);
       const difference = Math.abs(score_cal - 3);
       const rawValue = parseFloat(reportData[bacteria]) || 0;
       
       bacteriaScores.push({
         bacteria,
         rawValue,
         score_cal,
         difference
       });
       
       sumDifferences += difference;
       
       if (isAsthma) {
         debugLog(`${index + 1}. ${bacteria}:`);
         debugLog(`   Raw value: ${rawValue}`);
         debugLog(`   Score_cal: ${score_cal}`);
         debugLog(`   |${score_cal} - 3| = ${difference}`);
       }
     });
     
     if (isAsthma) {
       debugLog('Sum of differences:', sumDifferences);
       
       // Apply the formula: ⌈(9 × Σ|si - 3|)/(2n)⌉ + 1
       const multipliedSum = 9 * sumDifferences;
       debugLog('9 × sumDifferences:', multipliedSum);
       
       const dividedResult = multipliedSum / (2 * n);
       debugLog('Divided by 2n:', dividedResult);
       
       const ceilingResult = Math.ceil(dividedResult);
       debugLog('Ceiling result:', ceilingResult);
       
       let finalScore = ceilingResult + 1;
       
       // Cap the maximum score at 10
       if (finalScore > 10) {
         debugLog('Score capped at 10 (original score was:', finalScore, ')');
         finalScore = 10;
       }
       
       debugLog('Final score:', finalScore);
       debugLog('===============================');
     }
     
     // Apply the formula: ⌈(9 × Σ|si - 3|)/(2n)⌉ + 1
     const multipliedSum = 9 * sumDifferences;
     const dividedResult = multipliedSum / (2 * n);
     const ceilingResult = Math.ceil(dividedResult);
     let finalScore = ceilingResult + 1;
     
     // Cap the maximum score at 10
     if (finalScore > 10) {
       finalScore = 10;
     }
     
     return finalScore;
     
   } catch (error) {
     console.error('Error calculating static asthma score:', error);
     return 1; // fallback to Low instead of dropping the result
   }
 };

// Build a guaranteed fallback result for a config (used when calculation fails)
const buildFallbackResult = (scoreConfig) => ({
  level: 0,
  score: 1,
  range: { min: 0, max: 3 },
  title: scoreConfig.titel,
  match_key: scoreConfig.match_key || (Array.isArray(scoreConfig.titel) ? scoreConfig.titel.join(' ') : String(scoreConfig.titel)),
  color: '#4caf50',
  label: 'Low'
});

// Function to calculate score result based on formula and report data
const calculateScoreResult = (scoreConfig, reportData, panelFormat) => {
  try {
    if (scoreConfig.value_use && scoreConfig.formula) {
      let score = 0;

      if (scoreConfig.formula === 'static_asthma_score') {
        score = calculateStaticAsthmaScore(reportData, scoreConfig, panelFormat);
      } else {
        const values = scoreConfig.value_use.map((label, index) => {
          const raw = reportData[label];
          const val = raw !== undefined && raw !== '' ? parseFloat(raw) : 0;
          return { [`order${index + 1}`]: val };
        });
        const valueMap = Object.assign({}, ...values);
        const formulaEval = scoreConfig.formula.replace(/order\d+/g, (match) => valueMap[match] || 0);
        score = eval(formulaEval); // eslint-disable-line no-eval
      }

      // If score is null or NaN, use fallback Low (1) so all diseases always appear
      if (score === null || isNaN(score)) {
        score = 1;
      }

      const rounded = Number(score.toFixed(2));
      const matched = scoreConfig.table_socre ? scoreConfig.table_socre.find(r => rounded >= r.min && rounded <= r.max) : null;

      if (matched) {
        let level = 0;
        if (matched.label === 'Moderate' || matched.label === 'Moderate risk' || matched.label === 'Medium') {
          level = 1;
        } else if (matched.label === 'High' || matched.label === 'High risk') {
          level = 2;
        }

        debugLog(`🔍 Score mapping: score=${rounded}, matched range=${matched.min}-${matched.max}, label="${matched.label}", level=${level}`);

        let standardColor = '#4caf50';
        if (level === 1) standardColor = '#ff9800';
        else if (level === 2) standardColor = '#f44336';

        return {
          level,
          score: rounded,
          range: matched,
          title: scoreConfig.titel,
          match_key: scoreConfig.match_key,
          color: standardColor,
          label: matched.label
        };
      }

      // No range matched — return Low fallback so the disease still appears
      return buildFallbackResult(scoreConfig);
    }

    return buildFallbackResult(scoreConfig);
  } catch (error) {
    console.error('Error calculating score result:', error);
    return buildFallbackResult(scoreConfig);
  }
};

// Main function to calculate overall result level for a report
export const calculateReportResult = (panelFormat, reportData) => {
  try {
    // Validate inputs
    if (!panelFormat) {
      console.error('🔍 No panel format provided');
      return { level: 0, squares: 0, details: 'No panel format', scoreResults: [] };
    }
    
    if (!reportData || typeof reportData !== 'object') {
      console.error('🔍 Invalid report data provided');
      return { level: 0, squares: 0, details: 'Invalid report data', scoreResults: [] };
    }
    
    const scoreConfigs = getScoreConfig(panelFormat);
    debugLog(`🔍 Panel format: ${panelFormat}`);
    debugLog(`🔍 Score configs loaded:`, scoreConfigs);
    debugLog(`🔍 Report data sample:`, Object.keys(reportData).slice(0, 10));
    
    if (!scoreConfigs || scoreConfigs.length === 0) {
      debugLog(`🔍 No score configs available for panel: ${panelFormat}`);
      return { level: 0, squares: 0, details: `No score configs available for ${panelFormat}`, scoreResults: [] };
    }

         // Calculate results for each score configuration
     const scoreResults = scoreConfigs.map(config => 
       calculateScoreResult(config, reportData, panelFormat)
     ).filter(result => result !== null);

    // Deduplicate results by match_key to avoid showing same condition multiple times
    const uniqueResults = [];
    const seenKeys = new Set();
    
    scoreResults.forEach(result => {
      if (result && result.title && !seenKeys.has(result.title)) {
        uniqueResults.push(result);
        seenKeys.add(result.title);
      }
    });

    // Sort results by risk level: Green (0) → Orange (1) → Red (2) from left to right
    uniqueResults.sort((a, b) => a.level - b.level);

    // Generate summary report
    const successCount = scoreResults.filter(r => r !== null).length;
    const failCount = scoreResults.length - successCount;
    
    debugLog(`🔍 Score results calculated:`, scoreResults);
    debugLog(`🔍 Unique results after deduplication:`, uniqueResults);
    debugLog(`🔍 Results sorted by risk level (0=Green, 1=Orange, 2=Red)`);
    debugLog(`🔍 Summary: ${successCount} successful, ${failCount} failed calculations`);
    
    if (failCount > 0) {
      console.warn(`🔍 ${failCount} calculations failed - check bacteria data and meta configs`);
    }

    // Count squares based on unique score configurations
    const squares = uniqueResults.length;
    
    // Calculate overall level based on highest risk level found
    const maxLevel = uniqueResults.length > 0 ? Math.max(...uniqueResults.map(r => r.level)) : 0;
    
    // Create details string
    const details = `${panelFormat} format - ${squares} unique conditions calculated`;

    return {
      level: maxLevel,
      squares: squares,
      details: details,
      scoreResults: uniqueResults
    };

  } catch (error) {
    console.error('Error calculating report result:', error);
    return { level: 0, squares: 3, details: 'Calculation error', scoreResults: [] };
  }
};

// Function to get result colors based on level
export const getResultColors = (level, totalSquares = 3) => {
  const colors = ['#ffeb3b', '#ff9800', '#f44336']; // Yellow, Orange, Red
  
  // Return colors array with the appropriate number of colors
  return colors.slice(0, totalSquares);
};

// Function to check if a panel format is supported
export const isPanelSupported = (panelFormat) => {
  const supportedPanels = [
    'QU', 'QPT', 'Q9', '9P', 'QA', 'Allergy and Skin', 'QI', 'QA(KIDs)', 'Allergy and Skin_kids',
    'QG', 'Toxin', 'QM', 'oms', 'QBWCA', 'bwcallergy',
    'QBWCE', 'bwccore', 'QBWCU', 'bwcdiamond'
  ];
  
  return supportedPanels.includes(panelFormat);
};

// NEW: Row-by-row calculation function for better accuracy
export const calculateReportResultRowByRow = (panelFormat, reportData, reportId = 'unknown') => {
  try {
    debugLog(`🔍 [${reportId}] Starting ROW-BY-ROW calculation for ${panelFormat}`);
    
    // Validate inputs
    if (!panelFormat) {
      console.error(`🔍 [${reportId}] No panel format provided`);
      return { level: 0, squares: 0, details: 'No panel format', scoreResults: [] };
    }
    
    if (!reportData || typeof reportData !== 'object') {
      console.error(`🔍 [${reportId}] Invalid report data provided`);
      return { level: 0, squares: 0, details: 'Invalid report data', scoreResults: [] };
    }
    
         const allScoreConfigs = getScoreConfig(panelFormat);
     debugLog(`🔍 [${reportId}] Panel format: ${panelFormat}`);
     debugLog(`🔍 [${reportId}] All score configs loaded: ${allScoreConfigs.length} configurations`);
     
     if (!allScoreConfigs || allScoreConfigs.length === 0) {
       debugLog(`🔍 [${reportId}] No score configs available for panel: ${panelFormat}`);
       return { level: 0, squares: 0, details: `No score configs available for ${panelFormat}`, scoreResults: [] };
     }
     
     // DEDUPLICATE CONFIGS FIRST by match_key to avoid processing duplicates
     const uniqueConfigs = [];
     const seenMatchKeys = new Set();
     
     allScoreConfigs.forEach(config => {
       const matchKey = config.match_key || config.titel.toString();
       if (!seenMatchKeys.has(matchKey)) {
         uniqueConfigs.push(config);
         seenMatchKeys.add(matchKey);
         debugLog(`🔍 [${reportId}] ✅ Unique config added: ${config.titel} (Match Key: ${matchKey})`);
       } else {
         debugLog(`🔍 [${reportId}] ⚠️ Skipping duplicate config: ${config.titel} (Match Key: ${matchKey})`);
       }
     });
     
     const scoreConfigs = uniqueConfigs;
     debugLog(`🔍 [${reportId}] After deduplication: ${scoreConfigs.length} unique configs (was ${allScoreConfigs.length})`);
    
         // Calculate results ONE BY ONE — calculateScoreResult always returns a result now
     const seenResultKeys = new Set();
     const uniqueResults = [];

     scoreConfigs.forEach((config, configIndex) => {
       debugLog(`🔍 [${reportId}] Processing config ${configIndex + 1}/${scoreConfigs.length}: ${config.titel}`);
       try {
         const result = calculateScoreResult(config, reportData, panelFormat);
         // result is always non-null now (buildFallbackResult guarantees it)
         const matchKey = result.match_key || (Array.isArray(result.title) ? result.title.join(' ') : String(result.title));
         if (!seenResultKeys.has(matchKey)) {
           uniqueResults.push(result);
           seenResultKeys.add(matchKey);
           debugLog(`🔍 [${reportId}] ✅ ${config.titel}: Score=${result.score} Level=${result.level}`);
         }
       } catch (error) {
         // Last-resort fallback: add Low result directly from config
         const fallback = buildFallbackResult(config);
         const mk = fallback.match_key;
         if (!seenResultKeys.has(mk)) {
           uniqueResults.push(fallback);
           seenResultKeys.add(mk);
         }
         console.error(`🔍 [${reportId}] ❌ ${config.titel}: ERROR - ${error.message} (using Low fallback)`);
       }
     });

     debugLog(`🔍 [${reportId}] Summary: ${scoreConfigs.length} unique configs → ${uniqueResults.length} results`);

    // Sort by risk level: Green (0) → Orange (1) → Red (2)
    uniqueResults.sort((a, b) => a.level - b.level);

     const squares = uniqueResults.length;
     const maxLevel = uniqueResults.length > 0 ? Math.max(...uniqueResults.map(r => r.level)) : 0;
     const details = `${panelFormat} format - ${squares} conditions`;

    return {
      level: maxLevel,
      squares: squares,
      details: details,
      scoreResults: uniqueResults
    };
    
  } catch (error) {
    console.error(`🔍 [${reportId}] Error in row-by-row calculation:`, error);
    return { 
      level: 0, 
      squares: 0, 
      details: `Calculation error: ${error.message}`, 
      scoreResults: [],
      debug: { error: error.message }
    };
  }
};
